/**
 * DocumentImageAnnotatorComponent
 * The document-module image editor: a full-screen MatDialog that shows a document
 * image and lets the user draw arrows/shapes/text/measurements over it, then saves
 * the markup to the document (documents.markup) and exports an annotated PNG.
 *
 * Ported from the phases MediaGallerySection editor (its editor half only — no
 * gallery/upload). Persistence is document-scoped via DocumentMarkupService
 * (PATCH /document/{id}/markup); annotations are loaded from the document itself.
 */
import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener,
  Inject, OnDestroy, OnInit, ViewChild
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { UserService } from 'src/app/services/core/user.service';
import { ToasterService } from 'src/app/services/core/toaster.service';
import { FirestoreDocumentSyncService, DocumentSession } from 'src/app/services/firestore-document-sync.service';
import { stashCopiedImageName } from 'src/app/shared/utils/clipboard-image.util';
import { DocumentMarkupService } from './document-markup.data.service';
import { DocAnnotationCanvasComponent } from './doc-annotation-canvas.component';
import {
  Annotation, Comment, CommentPriority, CommentStatus, DrawingTool, Point
} from './media-annotation.types';

let cmtId = 0;
let txtId = 0;

/** A person currently viewing this image (for the header presence avatars). */
interface PresenceUser {
  id: number;
  name: string;
  initials: string;
  color: string;
  status?: 'online' | 'away' | 'offline';
}

@Component({
  standalone: false,
  selector: 'app-document-image-annotator',
  templateUrl: './document-image-annotator.component.html',
  styleUrls: ['./document-image-annotator.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DocumentImageAnnotatorComponent implements OnInit, OnDestroy {
  /** The image stage element — pan/zoom + export host. */
  @ViewChild('stageMedia') stageMedia?: ElementRef<HTMLElement>;
  /** The displayed <img> — drawn as the export base layer. */
  @ViewChild('stageImg') stageImg?: ElementRef<HTMLImageElement>;
  /** The SVG annotation canvas — re-measured once the image lays out. */
  @ViewChild(DocAnnotationCanvasComponent) annCanvas?: DocAnnotationCanvasComponent;

  /**
   * The image in a fresh dialog loads AFTER the canvas's first measure, so the
   * percent→pixel mapping can be stale (text/marks render at the wrong scale).
   * Re-measure the canvas once the image has actually laid out.
   */
  onImageLoad(): void {
    this.annCanvas?.updateSize();
    this.cdr.markForCheck();
  }

  // ---- Editor state ----
  activeTool: DrawingTool = 'select';
  selectedColor = '#FF6B35';
  strokeWidth = 4;
  opacity = 1;
  annotations: Annotation[] = [];
  comments: Comment[] = [];
  selectedAnnotationId: string | null = null;
  activeCommentId: string | null = null;
  showAnnotations = true;
  showComments = true;
  zoom = 1;
  pan: Point = { x: 0, y: 0 };
  panMode = false;
  exportError: string | null = null;
  newCommentPos: Point | null = null;
  newCommentText = '';
  // In-canvas text-annotation popup (replaces the browser prompt()).
  newTextPos: Point | null = null;
  newTextValue = '';
  // Armed "drop a comment" state. Separate from showComments (visibility): it's a
  // one-shot — the next click on the image opens the popup, then it disarms itself
  // so further clicks don't keep spawning comment popups.
  commentMode = false;

  private history: Annotation[][] = [[]];
  private historyIndex = 0;

  private isPanning = false;
  private panStart: Point = { x: 0, y: 0 };
  private spaceHeld = false;
  private markupDirty = false;
  private markupTimer: any;

  private readonly onSpaceDown = (e: KeyboardEvent): void => {
    if (e.code !== 'Space') return;
    const tag = (e.target as HTMLElement)?.tagName?.toLowerCase();
    if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
    e.preventDefault();
    if (!this.spaceHeld) { this.spaceHeld = true; this.panMode = true; this.cdr.markForCheck(); }
  };
  private readonly onSpaceUp = (e: KeyboardEvent): void => {
    if (e.code !== 'Space') return;
    this.spaceHeld = false;
    if (!this.isPanning) { this.panMode = false; this.cdr.markForCheck(); }
  };
  private readonly onWinPointerMove = (e: PointerEvent): void => this.onStagePointerMove(e);
  private readonly onWinPointerUp = (): void => this.onStagePointerUp();

  /** Current logged-in user's display name — stamped as the author on comments,
   *  replies and text annotations so the avatar/name shows who added them. */
  currentUserName = 'You';

  // ---- Presence (who else has this image open, same system as the PDF viewer) ----
  /** Unique id for THIS open tab, so we can exclude ourselves from the roster. */
  sessionId = `session_${Date.now()}_${Math.random().toString(36).substring(7)}`;
  currentUserForHeader: PresenceUser | null = null;
  collaboratorsForHeader: PresenceUser[] = [];
  private userId = 0;
  private userEmail = '';
  private presenceJoined = false;
  private sessionsSub?: Subscription;

  constructor(
    private cdr: ChangeDetectorRef,
    private api: DocumentMarkupService,
    private userService: UserService,
    private toaster: ToasterService,
    private firestoreSync: FirestoreDocumentSyncService,
    private dialogRef: MatDialogRef<DocumentImageAnnotatorComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    // getFullName() is `${first} ${last}`; strip null/undefined tokens from missing parts.
    const name = (this.userService.getFullName() || '')
      .replace(/\b(null|undefined)\b/g, '').replace(/\s+/g, ' ').trim();
    if (name) this.currentUserName = name;

    const idRaw = this.userService.getID();
    this.userId = idRaw ? parseInt(idRaw, 10) : 0;
    this.userEmail = this.userService.getEmail() || '';
    this.currentUserForHeader = {
      id: this.userId,
      name: this.currentUserName,
      initials: this.getInitials(this.currentUserName),
      color: '#f59e0b',   // amber for "you" (matches the PDF header)
    };
  }

  ngOnInit(): void {
    // The saved markup travels with the document (DocumentSettingResource emits
    // annotations/comments). Cloned so editing never mutates the caller's object.
    this.annotations = JSON.parse(JSON.stringify(this.data?.annotations ?? [])) as Annotation[];
    this.comments = JSON.parse(JSON.stringify(this.data?.comments ?? [])) as Comment[];
    this.history = [JSON.parse(JSON.stringify(this.annotations))];
    this.historyIndex = 0;
    // Restore the show/hide toggle state carried over from the last image (parity
    // with phases, where the single editor instance kept these across opens).
    this.showAnnotations = this.api.showAnnotations;
    this.showComments = this.api.showComments;

    // Load from the list item only (DocumentSettingResource carries the saved
    // annotations/comments). We deliberately do NOT fetch-on-open + auto-save:
    // a failed/empty fetch would wipe the in-memory set and the debounced save
    // would then clobber the real markup in the DB. Freshness across a same-
    // session reopen is handled by syncing the source list item after each save
    // (see flushMarkup / data.__sourceRef).
    window.addEventListener('keydown', this.onSpaceDown);
    window.addEventListener('keyup', this.onSpaceUp);

    this.joinPresence();
  }

  ngOnDestroy(): void {
    this.flushMarkup();
    this.leavePresence();
    window.removeEventListener('keydown', this.onSpaceDown);
    window.removeEventListener('keyup', this.onSpaceUp);
    window.removeEventListener('pointermove', this.onWinPointerMove);
    window.removeEventListener('pointerup', this.onWinPointerUp);
  }

  // ---- Document / image accessors ----
  get imageUrl(): string {
    return this.data?.url || this.data?.file_url || this.data?.attachment || '';
  }
  get docName(): string { return this.data?.original_name || this.data?.file_name || 'Image'; }
  /** File name without its extension — the extension is shown separately as a badge. */
  get docTitle(): string { return this.docName.replace(/\.[a-z0-9]+$/i, ''); }
  /** Dynamic type badge (JPG / PNG / GIF / WEBP…) parsed from the file name or URL. */
  get fileExtension(): string {
    const src = (this.docName || this.imageUrl || '').split('?')[0];
    const m = src.match(/\.([a-z0-9]+)$/i);
    return (m ? m[1] : (this.data?.file_type || 'IMG')).toString().toUpperCase();
  }
  /** Presence is keyed by the saved document id (matches the PDF viewer). */
  get documentId(): string { return this.data?.id != null ? String(this.data.id) : ''; }
  get uploadedBy(): string {
    return this.data?.creator?.name || this.data?.creator?.full_name || this.data?.uploaded_by || '—';
  }
  get uploadedAt(): string { return this.data?.created_at || ''; }
  get fileSize(): string {
    const s = this.data?.file_size;
    return (s === null || s === undefined || s === '') ? '—' : `${s} KB`;
  }
  get fileType(): string { return (this.data?.file_type || '').toString().toUpperCase() || '—'; }

  close(): void {
    this.flushMarkup();
    this.dialogRef.close();
  }

  // ---------- Presence (same FirestoreDocumentSyncService the PDF viewer uses) ----------
  private joinPresence(): void {
    const docId = this.documentId;
    if (!docId || !this.userId) return;   // need a saved document and a real user

    this.firestoreSync
      // presenceOnly=true: we only want the avatars roster, not the collaborative
      // editing operations/cursors listeners (this image viewer doesn't use them).
      .joinDocument(docId, this.sessionId, this.userId, this.currentUserName, this.userEmail, true)
      .then(() => { this.presenceJoined = true; })
      .catch(err => console.error('Presence join failed:', err));

    // Live roster of everyone in this document; drop our own session and de-dupe by user.
    this.sessionsSub = this.firestoreSync.sessions$.subscribe((sessions: DocumentSession[]) => {
      const seen = new Set<number>();
      const others: PresenceUser[] = [];
      for (const s of sessions || []) {
        // Skip our own session AND our own user id (we're already shown as "You"),
        // plus any duplicate sessions of the same person.
        if (s.sessionId === this.sessionId || s.userId === this.userId || seen.has(s.userId)) continue;
        seen.add(s.userId);
        others.push({
          id: s.userId,
          name: s.userName || 'User',
          initials: this.getInitials(s.userName || 'User'),
          color: this.colorForUser(s.userId || s.userName),
          status: s.status === 'offline' ? 'offline' : s.status === 'idle' ? 'away' : 'online',
        });
      }
      this.collaboratorsForHeader = others;
      this.cdr.markForCheck();
    });
  }

  private leavePresence(): void {
    this.sessionsSub?.unsubscribe();
    if (this.presenceJoined && this.documentId) {
      this.firestoreSync.leaveDocument(this.documentId, this.sessionId).catch(() => {});
      this.presenceJoined = false;
    }
  }

  /** Up to 3 other viewers shown as avatars; the rest collapse into a "+N" chip. */
  getDisplayedCollaborators(): PresenceUser[] { return this.collaboratorsForHeader.slice(0, 3); }
  trackByPresence = (_: number, u: PresenceUser): number => u.id;
  get extraCollaboratorCount(): number { return Math.max(0, this.collaboratorsForHeader.length - 3); }

  private getInitials(name: string): string {
    const parts = (name || '').trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return (name || 'U').substring(0, 2).toUpperCase();
  }

  /** Deterministic avatar color from a stable palette (so a user keeps one color). */
  private colorForUser(seed: string | number): string {
    const palette = ['#3b82f6', '#8b5cf6', '#ec4899', '#10b981', '#f97316', '#06b6d4', '#ef4444', '#84cc16'];
    const str = String(seed);
    let hash = 0;
    for (let i = 0; i < str.length; i++) hash = (hash * 31 + str.charCodeAt(i)) >>> 0;
    return palette[hash % palette.length];
  }

  // ---- Derived ----
  get selectedAnnotation(): Annotation | null {
    return this.annotations.find(a => a.id === this.selectedAnnotationId) || null;
  }
  get canUndo(): boolean { return this.historyIndex > 0; }
  get canRedo(): boolean { return this.historyIndex < this.history.length - 1; }

  // ---------- Toolbar ----------
  onTool(t: DrawingTool): void { this.activeTool = t; this.selectedAnnotationId = null; }
  onColor(c: string): void {
    this.selectedColor = c;
    if (this.selectedAnnotation) this.patchAnnotation(this.selectedAnnotation.id, { color: c });
  }
  onStroke(w: number): void { this.strokeWidth = w; }
  onOpacity(o: number): void { this.opacity = o; }
  toggleAnnotations(): void { this.showAnnotations = !this.showAnnotations; this.api.showAnnotations = this.showAnnotations; }
  toggleComments(): void { this.showComments = !this.showComments; this.api.showComments = this.showComments; }
  /** Arm/disarm one-shot comment placement. Arming also reveals comments (so you can
   *  see what you place) and drops any active drawing tool so the click drops a pin. */
  toggleAddComment(): void {
    this.commentMode = !this.commentMode;
    if (this.commentMode) {
      this.showComments = true;
      this.api.showComments = true;
      this.activeTool = 'select';
      this.selectedAnnotationId = null;
    }
  }

  isFullscreen = false;
  /** Expand the dialog to fill the window (100vw × 100vh) and back. Re-measures
   *  the canvas after the resize so annotations stay correctly scaled. */
  toggleFullscreen(): void {
    this.isFullscreen = !this.isFullscreen;
    if (this.isFullscreen) {
      this.dialogRef.addPanelClass('dia-fullscreen');
      this.dialogRef.updateSize('100vw', '100vh');
    } else {
      this.dialogRef.removePanelClass('dia-fullscreen');
      this.dialogRef.updateSize('92vw', '88vh');
    }
    // Stage size changed → let layout settle, then re-measure the SVG canvas.
    setTimeout(() => { this.annCanvas?.updateSize(); this.cdr.markForCheck(); }, 60);
    this.cdr.markForCheck();
  }
  zoomIn(): void { this.zoom = Math.min(5, +(this.zoom * 1.2).toFixed(2)); }
  zoomOut(): void { this.zoom = Math.max(0.1, +(this.zoom / 1.2).toFixed(2)); }
  get zoomPercent(): number { return Math.round(this.zoom * 100); }

  /** Reset zoom & pan to the default fit (rotation is preserved). */
  fitToScreen(): void {
    this.zoom = 1;
    this.pan = { x: 0, y: 0 };
    setTimeout(() => { this.annCanvas?.updateSize(); this.cdr.markForCheck(); }, 40);
    this.cdr.markForCheck();
  }

  // ---- Rotation (view control) ----
  rotation = 0;
  rotateLeft(): void { this.rotation -= 90; this.afterTransform(); }
  rotateRight(): void { this.rotation += 90; this.afterTransform(); }
  private afterTransform(): void {
    // The image + SVG rotate together (same wrapper), so marks stay aligned;
    // re-measure once the layout settles.
    setTimeout(() => { this.annCanvas?.updateSize(); this.cdr.markForCheck(); }, 40);
    this.cdr.markForCheck();
  }

  /** Combined transform for the media wrapper: pan → zoom → rotate (image + canvas). */
  get mediaTransform(): string {
    return `translate(${this.pan.x}px, ${this.pan.y}px) scale(${this.zoom}) rotate(${this.rotation}deg)`;
  }

  // ---- Edit mode: the drawing toolbar only shows when editing ----
  isEditMode = false;
  toggleEdit(): void {
    this.isEditMode = !this.isEditMode;
    if (!this.isEditMode) {
      // Leaving edit mode: drop any tool/selection so the viewer is clean.
      this.activeTool = 'select';
      this.selectedAnnotationId = null;
    }
    // The edit toolbar row changes the stage height → re-measure the canvas.
    setTimeout(() => { this.annCanvas?.updateSize(); this.cdr.markForCheck(); }, 40);
    this.cdr.markForCheck();
  }

  /**
   * Real PNG export: composite the displayed image + the live annotation SVG onto a
   * <canvas> and download `annotated-{id}.png`. Tainted-canvas (cross-origin) errors
   * are caught and surfaced as a soft notice. (Client-side; no API.)
   */
  export(): void {
    this.exportError = null;
    const imgEl = this.stageImg?.nativeElement;
    if (!imgEl) { this.exportError = 'Image is not ready for export.'; this.cdr.markForCheck(); return; }

    const svgEl = this.showAnnotations
      ? (this.stageMedia?.nativeElement.querySelector('svg.acv') as SVGSVGElement | null)
      : null;
    let svgUrl: string | null = null;
    if (svgEl) {
      const clone = svgEl.cloneNode(true) as SVGSVGElement;
      clone.setAttribute('width', String(imgEl.naturalWidth || imgEl.width));
      clone.setAttribute('height', String(imgEl.naturalHeight || imgEl.height));
      const xml = new XMLSerializer().serializeToString(clone);
      svgUrl = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(xml);
    }

    const base = new Image();
    base.crossOrigin = 'anonymous';
    base.onload = () => {
      const w = base.naturalWidth || imgEl.width;
      const h = base.naturalHeight || imgEl.height;
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) { this.noteExportFailure('Canvas not supported in this browser.'); return; }

      const finish = () => {
        try {
          canvas.toBlob(blob => {
            if (!blob) { this.noteExportFailure('Export failed to produce an image.'); return; }
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `annotated-${this.data?.id ?? 'image'}.png`;
            a.click();
            URL.revokeObjectURL(url);
          }, 'image/png');
        } catch {
          this.noteExportFailure('Unable to export: the image is cross-origin and cannot be saved.');
        }
      };

      try {
        ctx.drawImage(base, 0, 0, w, h);
      } catch {
        this.noteExportFailure('Unable to draw the image for export.');
        return;
      }

      if (!svgUrl) { finish(); return; }
      const overlay = new Image();
      overlay.onload = () => { ctx.drawImage(overlay, 0, 0, w, h); finish(); };
      overlay.onerror = () => finish();
      overlay.src = svgUrl;
    };
    base.onerror = () => this.noteExportFailure('Unable to load the image for export (CORS may be blocking it).');
    // Cache-buster: the DISPLAYED <img> (no crossorigin) may have cached a response
    // WITHOUT CORS headers. Reusing that cache entry for this crossOrigin export fetch
    // fails the CORS check. A unique URL forces a fresh CORS-mode request so S3 returns
    // the Access-Control-Allow-Origin header and the canvas stays untainted.
    const sep = this.imageUrl.includes('?') ? '&' : '?';
    base.src = `${this.imageUrl}${sep}dia_export=${Date.now()}`;
  }

  // ---------- Copy to clipboard (separate from export/download) ----------
  copied = false;

  /** Composite image + visible annotations to PNG and put it on the clipboard, so it
   *  can be pasted (Ctrl+V) into the CRM or anywhere. Export/download is unchanged. */
  copyToClipboard(): void {
    this.exportError = null;
    const CI: any = (window as any).ClipboardItem;
    const clip: any = navigator.clipboard;
    if (!clip || !CI || typeof clip.write !== 'function') {
      this.toaster.showErrorMessage('Copying images is not supported in this browser.');
      this.noteExportFailure('Copying images is not supported in this browser.');
      return;
    }
    try {
      // ClipboardItem accepts a Promise<Blob>; building it synchronously inside the
      // click keeps the user-activation the async clipboard write requires.
      const blobPromise = this.rasterizeForCopy();
      const item = new CI({ 'image/png': blobPromise });
      clip.write([item])
        .then(() => {
          // The clipboard can't carry a filename, so stash the original name for CRM
          // paste targets to recover (see clipboard-image.util / withResolvedName).
          blobPromise.then(blob => stashCopiedImageName(this.docName, blob.size)).catch(() => {});
          this.copied = true;
          this.toaster.showSuccessMessage('Image copied to clipboard');
          this.cdr.markForCheck();
          setTimeout(() => { this.copied = false; this.cdr.markForCheck(); }, 1500);
        })
        .catch((err: any) => {
          this.toaster.showErrorMessage(err?.message || 'Copy to clipboard failed.');
          this.noteExportFailure(err?.message || 'Copy to clipboard failed.');
        });
    } catch (err: any) {
      this.toaster.showErrorMessage(err?.message || 'Copy to clipboard failed.');
      this.noteExportFailure(err?.message || 'Copy to clipboard failed.');
    }
  }

  /** Same compositing as export(), but resolves to the PNG blob for the clipboard. */
  private rasterizeForCopy(): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const imgEl = this.stageImg?.nativeElement;
      if (!imgEl) { reject(new Error('Image is not ready.')); return; }

      const svgEl = this.showAnnotations
        ? (this.stageMedia?.nativeElement.querySelector('svg.acv') as SVGSVGElement | null)
        : null;
      let svgUrl: string | null = null;
      if (svgEl) {
        const clone = svgEl.cloneNode(true) as SVGSVGElement;
        clone.setAttribute('width', String(imgEl.naturalWidth || imgEl.width));
        clone.setAttribute('height', String(imgEl.naturalHeight || imgEl.height));
        const xml = new XMLSerializer().serializeToString(clone);
        svgUrl = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(xml);
      }

      const base = new Image();
      base.crossOrigin = 'anonymous';
      base.onload = () => {
        const w = base.naturalWidth || imgEl.width;
        const h = base.naturalHeight || imgEl.height;
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) { reject(new Error('Canvas not supported in this browser.')); return; }

        const finish = () => {
          try {
            canvas.toBlob(blob => {
              blob ? resolve(blob) : reject(new Error('Failed to produce an image.'));
            }, 'image/png');
          } catch {
            reject(new Error('The image is cross-origin and cannot be copied.'));
          }
        };

        try { ctx.drawImage(base, 0, 0, w, h); }
        catch { reject(new Error('Unable to draw the image.')); return; }

        if (!svgUrl) { finish(); return; }
        const overlay = new Image();
        overlay.onload = () => { ctx.drawImage(overlay, 0, 0, w, h); finish(); };
        overlay.onerror = () => finish();
        overlay.src = svgUrl;
      };
      base.onerror = () => reject(new Error('Unable to load the image (CORS may be blocking it).'));
      const sep = this.imageUrl.includes('?') ? '&' : '?';
      base.src = `${this.imageUrl}${sep}dia_copy=${Date.now()}`;
    });
  }

  private noteExportFailure(msg: string): void {
    console.warn('[doc-annotator] export', msg, { documentId: this.data?.id });
    this.exportError = msg;
    this.cdr.markForCheck();
  }

  // ---------- Stage pan + ctrl-wheel zoom ----------
  private get canPan(): boolean {
    return this.spaceHeld || (this.activeTool === 'select' && !this.selectedAnnotationId);
  }
  onStagePointerDown(e: PointerEvent): void {
    if (e.button !== 0 || !this.canPan) return;
    this.isPanning = true;
    this.panMode = true;
    this.panStart = { x: e.clientX - this.pan.x, y: e.clientY - this.pan.y };
    window.addEventListener('pointermove', this.onWinPointerMove);
    window.addEventListener('pointerup', this.onWinPointerUp);
    this.cdr.markForCheck();
  }
  private onStagePointerMove(e: PointerEvent): void {
    if (!this.isPanning) return;
    this.pan = { x: e.clientX - this.panStart.x, y: e.clientY - this.panStart.y };
    this.cdr.markForCheck();
  }
  private onStagePointerUp(): void {
    if (!this.isPanning) return;
    this.isPanning = false;
    if (!this.spaceHeld) this.panMode = false;
    window.removeEventListener('pointermove', this.onWinPointerMove);
    window.removeEventListener('pointerup', this.onWinPointerUp);
    this.cdr.markForCheck();
  }
  onStageWheel(e: WheelEvent): void {
    if (!e.ctrlKey) return;
    e.preventDefault();
    const delta = e.deltaY > 0 ? 1 / 1.1 : 1.1;
    this.zoom = +Math.min(5, Math.max(0.1, this.zoom * delta)).toFixed(3);
    this.cdr.markForCheck();
  }

  // ---------- Canvas ----------
  onAddAnnotation(a: Annotation): void {
    this.annotations = [...this.annotations, a];
    this.pushHistory();
    this.selectedAnnotationId = a.id;
    this.activeTool = 'select';
    this.saveMarkupDebounced();
  }
  onUpdateAnnotation(e: { id: string; changes: Partial<Annotation> }): void {
    this.patchAnnotation(e.id, e.changes);
  }
  private patchAnnotation(id: string, changes: Partial<Annotation>): void {
    this.annotations = this.annotations.map(a => a.id === id ? { ...a, ...changes } : a);
  }
  onSelectAnnotation(id: string | null): void {
    this.selectedAnnotationId = id;
    if (id) this.activeCommentId = null;
  }
  onPropUpdate(changes: Partial<Annotation>): void {
    if (this.selectedAnnotationId) this.patchAnnotation(this.selectedAnnotationId, changes);
  }
  deleteSelectedAnnotation(): void {
    if (!this.selectedAnnotationId) return;
    this.annotations = this.annotations.filter(a => a.id !== this.selectedAnnotationId);
    this.selectedAnnotationId = null;
    this.pushHistory();
    this.saveMarkupDebounced();
  }

  private pushHistory(): void {
    this.history = [...this.history.slice(0, this.historyIndex + 1), JSON.parse(JSON.stringify(this.annotations))];
    this.historyIndex = this.history.length - 1;
  }
  undo(): void {
    if (!this.canUndo) return;
    this.historyIndex--;
    this.annotations = JSON.parse(JSON.stringify(this.history[this.historyIndex]));
    this.selectedAnnotationId = null;
    this.saveMarkupDebounced();
  }
  redo(): void {
    if (!this.canRedo) return;
    this.historyIndex++;
    this.annotations = JSON.parse(JSON.stringify(this.history[this.historyIndex]));
    this.saveMarkupDebounced();
  }

  // ---------- Comments ----------
  onBackgroundClick(pt: Point): void {
    this.selectedAnnotationId = null;
    if (this.commentMode) { this.newCommentPos = pt; this.newCommentText = ''; }
  }
  addComment(): void {
    if (!this.newCommentPos || !this.newCommentText.trim()) return;
    const c: Comment = {
      id: `cmt-${Date.now()}-${cmtId++}`,
      x: this.newCommentPos.x, y: this.newCommentPos.y,
      text: this.newCommentText.trim(), author: this.currentUserName,
      timestamp: new Date().toISOString(), status: 'open', priority: 'medium', replies: []
    };
    this.comments = [...this.comments, c];
    this.activeCommentId = c.id;
    this.newCommentPos = null;
    this.newCommentText = '';
    this.commentMode = false;   // one-shot: disarm after placing
    this.saveMarkupDebounced();
  }
  cancelComment(): void { this.newCommentPos = null; this.newCommentText = ''; this.commentMode = false; }

  // ---------- Text annotation (in-canvas popup) ----------
  onTextRequested(pt: Point): void {
    this.newTextPos = pt;
    this.newTextValue = '';
    this.cdr.markForCheck();
  }
  addTextAnnotation(): void {
    if (!this.newTextPos || !this.newTextValue.trim()) return;
    const a: Annotation = {
      id: `ann-${Date.now()}-${txtId++}`,
      type: 'text',
      color: this.selectedColor,
      strokeWidth: this.strokeWidth,
      opacity: this.opacity,
      author: this.currentUserName,
      timestamp: new Date().toISOString(),
      start: this.newTextPos,
      text: this.newTextValue.trim(),
      fontSize: 24,
    };
    this.onAddAnnotation(a);   // pushes, selects, resets tool to select, saves
    this.newTextPos = null;
    this.newTextValue = '';
  }
  cancelText(): void { this.newTextPos = null; this.newTextValue = ''; this.activeTool = 'select'; }
  selectCommentPin(id: string | null): void { this.activeCommentId = id; this.selectedAnnotationId = null; }
  deleteComment(id: string, event?: Event): void {
    if (event) event.stopPropagation();
    this.comments = this.comments.filter(c => c.id !== id);
    if (this.activeCommentId === id) this.activeCommentId = null;
    this.saveMarkupDebounced();
  }
  commentPinClass(status: CommentStatus): string {
    return { open: 'pin--open', in_progress: 'pin--progress', resolved: 'pin--resolved', closed: 'pin--closed' }[status];
  }

  // ---------- Comment sidebar (mirrors the phases editor) ----------
  /** Right rail shows the comment thread panel when comments are on, nothing is
   *  selected, and there is at least one comment (or one is active). */
  get showCommentSidebar(): boolean {
    return this.showComments && !this.selectedAnnotationId
      && (!!this.activeCommentId || this.comments.length > 0);
  }
  onCommentReply(e: { commentId: string; text: string }): void {
    this.comments = this.comments.map(c => c.id === e.commentId
      ? { ...c, replies: [...c.replies, { id: `r-${Date.now()}`, text: e.text, author: this.currentUserName, timestamp: new Date().toISOString() }] }
      : c);
    this.saveMarkupDebounced();
  }
  onCommentStatus(e: { id: string; status: CommentStatus }): void {
    this.comments = this.comments.map(c => c.id === e.id ? { ...c, status: e.status } : c);
    this.saveMarkupDebounced();
  }
  onCommentPriority(e: { id: string; priority: CommentPriority }): void {
    this.comments = this.comments.map(c => c.id === e.id ? { ...c, priority: e.priority } : c);
    this.saveMarkupDebounced();
  }

  // ---------- Persistence ----------
  /** Debounced: a pen stroke fires several edits; batch them into one PATCH. */
  private saveMarkupDebounced(): void {
    if (!this.data?.id) { return; }
    this.markupDirty = true;
    clearTimeout(this.markupTimer);
    this.markupTimer = setTimeout(() => this.flushMarkup(), 600);
  }
  private flushMarkup(): void {
    clearTimeout(this.markupTimer);
    if (!this.markupDirty || !this.data?.id) { return; }
    const annotations = this.annotations;
    const comments = this.comments;
    this.markupDirty = false;
    this.api.saveMarkup(this.data.id, annotations, comments).subscribe({
      next: () => {
        // Keep the caller's object in step so reopening restores the same markup.
        this.data.annotations = annotations;
        this.data.comments = comments;
        // Also sync the ORIGINAL folder-list item (passed as __sourceRef) so a
        // same-session close→reopen shows the latest markup without the folder
        // needing to re-fetch. Safe: only touches these two fields.
        if (this.data.__sourceRef) {
          this.data.__sourceRef.annotations = annotations;
          this.data.__sourceRef.comments = comments;
        }
      },
      error: () => { this.markupDirty = true; }
    });
  }

  // ---------- Keyboard shortcuts ----------
  @HostListener('document:keydown', ['$event'])
  onKey(e: KeyboardEvent): void {
    const tag = (e.target as HTMLElement)?.tagName?.toLowerCase();
    if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
    const k = e.key.toLowerCase();
    if ((e.ctrlKey || e.metaKey) && k === 'z') { e.preventDefault(); e.shiftKey ? this.redo() : this.undo(); return; }
    if ((e.ctrlKey || e.metaKey) && k === 'y') { e.preventDefault(); this.redo(); return; }
    if (k === 'delete' || k === 'backspace') { if (this.selectedAnnotationId) this.deleteSelectedAnnotation(); return; }
    if (k === 'escape') { this.close(); return; }
    const map: Record<string, DrawingTool> = { v: 'select', p: 'pen', a: 'arrow', r: 'rectangle', c: 'circle', t: 'text', m: 'ruler' };
    if (map[k]) this.onTool(map[k]);
  }

  trackByComment(_: number, c: Comment): string { return c.id; }
}
